/**
 * Create a promise we can manually resolve / reject later, with associated callbacks
 */
const createQueuedPromise = <T extends any>(
    createPromise: () => Promise<T>
): [() => Promise<T>, Promise<T>] => {
    let resolvePendingPromise: (val: T) => void;
    let rejectPendingPromise: (e: any) => void;

    // This is the promise that we pass back to consumer, and we don't resolve it
    // until after the actual work that has been done by the `createPromise` promise has resolved.
    //
    // tslint:disable-next-line:promise-must-complete
    const pendingPromise = new Promise<T>((resolve, reject) => {
        // Promises run their bodies immediately, so the resolve / reject callbacks will be bound
        // in the parent scope no matter what
        resolvePendingPromise = resolve;
        rejectPendingPromise = reject;
    });

    const triggerPromiseEvaluation = () => {
        const taskPromise = createPromise();
        // Finish this out-of-band so we don't block the next item in the queue being
        // processed by the consumer handling taskPromise
        // (in `resolvePendingPromise` / `rejectPendingPromise` here).
        taskPromise.then(resolvePendingPromise, rejectPendingPromise);
        return taskPromise;
    };

    return [triggerPromiseEvaluation, pendingPromise];
};

/**
 * A queue of tasks that will defer execution of a given promise
 * until some fixed number of promises have resolved.
 */
export class WaitingQueue<T> {
    private pendingCallbacks: (() => Promise<T>)[] = [];
    private ongoingCallbacks = 0;
    private queueSize: number;
    constructor(queueSize: number) {
        this.queueSize = queueSize;
    }

    public queueTask(createPromise: () => Promise<T>) {
        if (this.ongoingCallbacks >= this.queueSize) {
            const [triggerPromiseEvaluation, promiseToReturn] = createQueuedPromise(createPromise);
            this.pendingCallbacks.push(triggerPromiseEvaluation);
            return promiseToReturn;
        } else {
            this.ongoingCallbacks++;
            const taskPromise = createPromise();
            // as a side-effect on promise resolution, queue the next task if available
            taskPromise.then(this.handlePromiseResolution);
            return taskPromise;
        }
    }

    private handlePromiseResolution = () => {
        this.ongoingCallbacks--;
        if (this.ongoingCallbacks < this.queueSize && this.pendingCallbacks.length) {
            const triggerQueuedPromise = this.pendingCallbacks.shift();
            this.ongoingCallbacks++;
            if (triggerQueuedPromise) {
                triggerQueuedPromise().then(this.handlePromiseResolution);
            }
        }
    };
}
